import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryContext = createContext(null);

// Read UTM params from URL and persist in sessionStorage so they survive across route/anchor changes
function captureUtm() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
  const hasAny = Object.values(utm).some(Boolean);
  if (hasAny) {
    try { sessionStorage.setItem('mbe_utm', JSON.stringify(utm)); } catch (e) {}
    return utm;
  }
  try {
    const stored = sessionStorage.getItem('mbe_utm');
    if (stored) return JSON.parse(stored);
  } catch (e) {}
  return utm;
}

export function CountryProvider({ children }) {
  const [country, setCountry] = useState(null); // { code, name, city }
  const [config, setConfig] = useState({ default_url: 'https://t.me/MBEInternational', country_links: [] });
  const [ready, setReady] = useState(false);
  const [utm, setUtm] = useState({});

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 0) Capture UTM parameters from URL
      const captured = captureUtm();
      if (!cancelled) setUtm(captured);

      // 1) Detect country client-side via ipapi.co (free, no key)
      let detected = { code: null, name: null, city: null };
      try {
        const r = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
        if (r.ok) {
          const j = await r.json();
          detected = {
            code: (j.country_code || j.country || '').toUpperCase() || null,
            name: j.country_name || null,
            city: j.city || null,
          };
        }
      } catch (e) {
        // ignore, fall back to no country
      }

      // 2) Load link config from backend
      try {
        const c = await axios.get(`${API}/config`);
        if (!cancelled) setConfig(c.data);
      } catch (e) {
        // keep default
      }

      if (!cancelled) {
        setCountry(detected);
        setReady(true);
      }

      // 3) Fire our own PageView event (in addition to Meta Pixel) so the
      //    admin dashboard has PageView counts too.
      try {
        const utmForPv = captured;
        axios.post(`${API}/track/pageview`, {
          country_code: detected.code || null,
          country_name: detected.name || null,
          city: detected.city || null,
          utm_source: utmForPv?.utm_source || null,
          utm_medium: utmForPv?.utm_medium || null,
          utm_campaign: utmForPv?.utm_campaign || null,
          utm_content: utmForPv?.utm_content || null,
          utm_term: utmForPv?.utm_term || null,
          referrer: (typeof document !== 'undefined' ? document.referrer : null) || null,
          path: (typeof window !== 'undefined' ? window.location.pathname : null) || null,
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  // Resolve which telegram URL applies to the detected country
  function resolveUrl() {
    const code = country?.code;
    if (code && Array.isArray(config.country_links)) {
      const m = config.country_links.find((c) => (c.code || '').toUpperCase() === code && c.is_active !== false);
      if (m) return m.url;
    }
    return config.default_url;
  }

  const value = {
    country,
    config,
    ready,
    utm,
    telegramUrl: resolveUrl(),
    isMapped: !!(country?.code && (config.country_links || []).some((c) => (c.code || '').toUpperCase() === country.code)),
  };

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error('useCountry must be used inside CountryProvider');
  return ctx;
}

// Convenient click handler: fires Meta Pixel Subscribe + logs click to backend with UTM
export function useCtaHandler() {
  const { country, telegramUrl, utm } = useCountry();

  return function trackAndGo(source) {
    // Meta Pixel Subscribe event
    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Subscribe', {
          content_name: 'Road To Bali Challenge',
          country: country?.code || 'UNKNOWN',
          source: source || 'cta',
          utm_source: utm?.utm_source || undefined,
          utm_campaign: utm?.utm_campaign || undefined,
          utm_content: utm?.utm_content || undefined,
        });
      }
    } catch (e) { /* ignore */ }

    // Backend click log
    try {
      const referrer = typeof document !== 'undefined' ? document.referrer || null : null;
      axios.post(`${API}/track/click`, {
        country_code: country?.code || null,
        country_name: country?.name || null,
        city: country?.city || null,
        telegram_url: telegramUrl,
        cta_source: source || 'cta',
        utm_source: utm?.utm_source || null,
        utm_medium: utm?.utm_medium || null,
        utm_campaign: utm?.utm_campaign || null,
        utm_content: utm?.utm_content || null,
        utm_term: utm?.utm_term || null,
        referrer,
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  };
}
